import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpHeaders
} from '@angular/common/http';

import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  IDAM_TOKEN,
  REDIRECT_ON_UNAUTHORIZE,
  REFRESH_TOKEN
} from 'src/app/constants';
import { UtilService } from './util.service';
import { LocationService } from './location.service';
import { ProfileService } from './profile.service';
import { TierThemeService } from './tier-theme.service';
import {
  RetailIDAMRefreshTokenResponse,
  RetailIDAMResponse
} from '../interfaces/idam-auth.interfaces';

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface AuthInfo {
  jwt?: string;
  accessTokenExpiresAt?: number;
  refreshToken?: string;
  refreshTokenExpiresAt?: number;
  [key: string]: any;
}

interface JcnInfo {
  jwt?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authInfo: AuthInfo = {};
  private jcnInfo: JcnInfo = {};
  public isLoggedIn$ = new BehaviorSubject<boolean>(false);
  public redirectURL?: string;
  private fetchingRefreshToken = false;

  constructor(
    private util: UtilService,
    private http: HttpClient,
    private locationService: LocationService,
    private profileService: ProfileService,
    private tierThemeService: TierThemeService
  ) {}

  setAuthInfo(info: AuthInfo) {
    this.authInfo = info;
    localStorage['authInfo'] = JSON.stringify(info);
  }

  setJcnInfo(info: JcnInfo) {
    this.jcnInfo = info;
    localStorage['jcnInfo'] = JSON.stringify(info);
  }

  getAuthInfo(): AuthInfo {
    const authInfo = localStorage['authInfo'] as string;
    if (authInfo) {
      return JSON.parse(authInfo) as AuthInfo;
    }
    return this.authInfo;
  }

  getJcnInfo(): JcnInfo {
    const jcnInfo = localStorage['jcnInfo'] as string;
    if (jcnInfo) {
      return JSON.parse(jcnInfo) as JcnInfo;
    }
    return this.jcnInfo;
  }

  getIsAuthenticated(): boolean {
    const authInfo = this.getAuthInfo();
    return !!authInfo.jwt;
  }

  getJcnAuthenticated(): boolean {
    const jcnInfo = this.getJcnInfo();
    return !!jcnInfo.jwt;
  }

  setAuthToken(token: string) {
    this.authInfo.jwt = token;
  }

  setJcnToken(token: string) {
    this.jcnInfo.jwt = token;
  }

  getAuthToken(): string {
    const authInfo = this.getAuthInfo();
    return authInfo.jwt ? authInfo.jwt : '';
  }

  getJcnToken(): string {
    const jcnInfo = this.getJcnInfo();
    return jcnInfo.jwt ? jcnInfo.jwt : '';
  }

  logout() {
    this.isLoggedIn$.next(false);
    this.setAuthInfo({});
    this.setJcnInfo({});
    this.clearIDAMAuthToken();
    this.clearMonthlyReportToken();
    this.locationService.clearLocationInfo();
    this.profileService.clearProfileInfo();
    this.tierThemeService.resetToDefault();
  }

  getRefreshToken() {
    const authInfo = this.getAuthInfo();
    return authInfo.refreshToken ?? '';
  }

  setIDAMAuthToken(token: string): void {
    localStorage.setItem('idamAccessToken', token);
  }

  getIDAMAuthToken(): string {
    return localStorage.getItem('idamAccessToken') || '';
  }

  clearIDAMAuthToken(): void {
    localStorage.removeItem('idamAccessToken');
  }

  setMonthlyReportToken(token: string) {
    localStorage.setItem('monthlyReportToken', token);
  }

  getMonthlyReportToken(): string {
    return localStorage.getItem('monthlyReportToken') as string;
  }

  clearMonthlyReportToken() {
    localStorage.removeItem('monthlyReportToken');
  }

  validateIDAMSession(
    clientId: string,
    idamAccessToken: string
  ): Observable<{ success: boolean }> {
    if (
      !environment.retailIDAMBackendURL ||
      !environment.clientIdForRetailIDAM
    ) {
      return of({ success: false });
    }

    const url = `${environment.retailIDAMBackendURL}/service/application/retail-auth/v2.0/validate-token`;

    const headers = new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      client_id: clientId,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Authorization: `Bearer ${idamAccessToken}`,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json'
    });

    return this.http
      .post<RetailIDAMResponse>(
        url,
        {},
        {
          headers,
          context: new HttpContext()
            .set(IDAM_TOKEN, true)
            .set(REDIRECT_ON_UNAUTHORIZE, false)
        }
      )
      .pipe(
        map(() => {
          return { success: true };
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 401) {
            console.error(
              'Unauthorized - Invalid credentials or authorization issue',
              err.error
            );
          } else if (err.status === 400) {
            console.error(
              'Bad Request - Possibly invalid parameters',
              err.error
            );
          }
          return of({ success: false });
        })
      );
  }

  refreshAccessToken() {
    const url = `${environment.apiHost}/auth/phone/refresh-token`;

    return this.http
      .post<RetailIDAMRefreshTokenResponse>(
        url,
        {},
        {
          context: new HttpContext()
            .set(REFRESH_TOKEN, true)
            .set(REDIRECT_ON_UNAUTHORIZE, false)
        }
      )
      .pipe(
        map((response) => {
          this.setAuthToken(response.accessToken);
          this.setAuthInfo({
            jwt: response.accessToken,
            accessTokenExpiresAt:
              new Date().getTime() + response.accessTokenExpiresIn * 1000,
            refreshToken: response.refreshToken,
            refreshTokenExpiresAt:
              new Date().getTime() + response.refreshTokenExpiresIn * 1000,
            info: this.util.parseJWT(response.accessToken)
          });

          this.fetchingRefreshToken = false;
          return { success: true };
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 401) {
            console.error(
              'Unauthorized - Invalid credentials or authorization issue',
              err.error
            );
          } else if (err.status === 400) {
            console.error(
              'Bad Request - Possibly invalid parameters',
              err.error
            );
          }
          this.fetchingRefreshToken = false;

          return of({ success: false });
        })
      );
  }

  refreshIdamAccessToken() {
    const url = `${environment.apiHost}/auth/retail-idam/refresh-token`;

    return this.http
      .post<RetailIDAMRefreshTokenResponse>(
        url,
        {},
        {
          context: new HttpContext()
            .set(REFRESH_TOKEN, true)
            .set(REDIRECT_ON_UNAUTHORIZE, false)
        }
      )
      .pipe(
        map((response) => {
          this.setIDAMAuthToken(response.idamAccessToken);
          this.setAuthToken(response.accessToken);
          this.setAuthInfo({
            jwt: response.accessToken,
            accessTokenExpiresAt:
              new Date().getTime() + response.accessTokenExpiresIn * 1000,
            refreshToken: response.refreshToken,
            refreshTokenExpiresAt:
              new Date().getTime() + response.refreshTokenExpiresIn * 1000,
            info: this.util.parseJWT(response.accessToken)
          });

          this.fetchingRefreshToken = false;
          return { success: true };
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 401) {
            console.error(
              'Unauthorized - Invalid credentials or authorization issue',
              err.error
            );
          } else if (err.status === 400) {
            console.error(
              'Bad Request - Possibly invalid parameters',
              err.error
            );
          }
          this.fetchingRefreshToken = false;

          return of({ success: false });
        })
      );
  }

  validateSession(): Observable<boolean> {
    const clientId = environment.clientIdForRetailIDAM;
    const authInfo = this.getAuthInfo();
    const currentDateTime = new Date().getTime();

    if (!environment.retailIDAMBackendURL || !clientId) {
      if (
        !authInfo ||
        !authInfo.accessTokenExpiresAt ||
        !authInfo.refreshTokenExpiresAt ||
        !authInfo.refreshToken
      ) {
        this.logout();
        return of(false);
      }

      if (currentDateTime < authInfo.accessTokenExpiresAt) {
        return of(true);
      } else if (currentDateTime < authInfo.refreshTokenExpiresAt) {
        if (!this.fetchingRefreshToken) {
          this.fetchingRefreshToken = true;

          return this.refreshAccessToken().pipe(
            map((response) => {
              const isValid = response.success;
              if (!isValid) {
                this.logout();
              }

              return isValid;
            }),
            catchError(() => {
              this.logout();

              return of(false);
            })
          );
        } else {
          return this.waitForOngoingRefresh();
        }
      } else {
        this.logout();
        return of(false);
      }
    }

    const idamAccessToken = this.getIDAMAuthToken();

    if (!idamAccessToken) {
      this.isLoggedIn$.next(false);
      return of(true);
    }

    if (
      !authInfo ||
      !authInfo.accessTokenExpiresAt ||
      !authInfo.refreshTokenExpiresAt ||
      !authInfo.refreshToken
    ) {
      this.isLoggedIn$.next(false);
      return of(false);
    }

    return this.validateIDAMSession(clientId, idamAccessToken).pipe(
      switchMap((response) => {
        const isValid = response.success;

        if (isValid) {
          return of(isValid);
        }

        if (currentDateTime < (authInfo.refreshTokenExpiresAt || 0)) {
          if (!this.fetchingRefreshToken) {
            this.fetchingRefreshToken = true;

            return this.refreshIdamAccessToken().pipe(
              map((response) => {
                if (!response.success) {
                  this.clearIDAMAuthToken();
                  this.isLoggedIn$.next(false);
                }
                return response.success;
              }),
              catchError(() => {
                this.clearIDAMAuthToken();
                this.isLoggedIn$.next(false);

                return of(false);
              })
            );
          } else {
            return this.waitForOngoingRefresh();
          }
        }

        this.clearIDAMAuthToken();
        this.isLoggedIn$.next(false);
        return of(isValid);
      }),
      catchError(() => {
        this.clearIDAMAuthToken();
        this.isLoggedIn$.next(false);
        return of(false);
      })
    );
  }

  /**
   * Polls `fetchingRefreshToken` every 200ms while another caller is
   * refreshing the token. Emits `true` and completes when the refresh
   * finishes, or after ~2.2s as a safety timeout.
   *
   * Uses RxJS `timer` instead of a hand-rolled `new Observable + setInterval`,
   * because the latter can leak its subscription out of NgZone, suppressing
   * change detection in any HTTP response that races with token validation.
   */
  private waitForOngoingRefresh(): Observable<boolean> {
    return timer(200, 200).pipe(
      filter((count) => !this.fetchingRefreshToken || count > 10),
      take(1),
      map(() => true)
    );
  }
}
